import { useTranslation } from "react-i18next";
import type { ContentFilterTrendPoint } from "@/api";
import { PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { formatDate, formatNumber } from "@/i18n/format";

// The API buckets by UTC day; formatting a day in local time would show the day before, west of UTC.
const formatDay = (day: string) => formatDate(day, { dateStyle: "medium", timeZone: "UTC" });

/** Category hits per UTC day; the data is also in a visually hidden table for screen readers. */
export function HitsTrend({ days }: Readonly<{ days: ContentFilterTrendPoint[] }>) {
  const { t } = useTranslation("adminContentSafety");
  const max = Math.max(1, ...days.map((day) => day.hits));
  const first = days.at(0);
  const last = days.at(-1);
  return (
    <PageSection title={t("trend.title")} description={t("trend.description")}>
      {/* `relative` keeps the sr-only (absolute) table inside the page's scroller; otherwise it makes the document taller. */}
      <div className="relative flex flex-col gap-4">
        <div className="flex h-24 items-end gap-0.5" aria-hidden="true">
          {days.map((day) => (
            <div
              key={day.day}
              title={t("trend.bar", {
                count: day.hits,
                date: formatDay(day.day),
                hits: formatNumber(day.hits),
                blocked: formatNumber(day.blocked),
              })}
              className="min-w-0 flex-1 rounded-t-sm bg-brand-gradient"
              style={{ height: `${Math.max(2, (day.hits / max) * 100)}%` }}
            />
          ))}
        </div>
        {first && last && (
          <div className="flex justify-between" aria-hidden="true">
            <Text as="span" variant="caption" tone="tertiary">
              {formatDay(first.day)}
            </Text>
            <Text as="span" variant="caption" tone="tertiary">
              {formatDay(last.day)}
            </Text>
          </div>
        )}
        <table className="sr-only">
          <caption>{t("trend.table")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("trend.day")}</th>
              <th scope="col">{t("trend.hits")}</th>
              <th scope="col">{t("trend.blocked")}</th>
            </tr>
          </thead>
          <tbody>
            {days.map((day) => (
              <tr key={day.day}>
                <td>{formatDay(day.day)}</td>
                <td>{formatNumber(day.hits)}</td>
                <td>{formatNumber(day.blocked)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageSection>
  );
}
